import {
    Card,
    Player,
    HoldemGameState,
    GamePhase,
    PlayerAction,
    HandResult,
    PotWinner,
    AIDecision,
    PlayerPersonality,
    BlindConfiguration
} from '../types/holdem.types';
import { getPlayerDialogue } from '../data/playerDialogue';

export class TexasHoldemEngine {
    private state: HoldemGameState;
    private blindConfig: BlindConfiguration;
    private handsSinceLastBlindIncrease: number = 0;

    constructor(playerChips: number = 1000, playerName: string = 'You', blindConfig?: BlindConfiguration) {
        // Use provided config or create default
        this.blindConfig = blindConfig || {
            mode: 'default',
            initialSmallBlind: 10,
            initialBigBlind: 20,
            increaseFrequency: 'elimination',
            increaseMultiplier: 2
        };
        this.state = this.initializeGame(playerChips, playerName);
    }

    private initializeGame(playerChips: number, playerName: string): HoldemGameState {
        // Calculate blinds based on configuration
        let smallBlind: number;
        let bigBlind: number;

        if (this.blindConfig.mode === 'default') {
            // Default mode: tier-based calculation from player balance
            let tier = 0;
            if (playerChips <= 1000) {
                tier = 0;
            } else {
                tier = Math.ceil((playerChips - 1000) / 1000);
            }
            smallBlind = (tier + 1) * 10;
            bigBlind = (tier + 1) * 20;
            console.log(`[TexasHoldemEngine] Default mode - Balance: $${playerChips}, Tier: ${tier}, Blinds: $${smallBlind}/$${bigBlind}`);
        } else {
            // Fixed or Custom mode: use configured blinds
            smallBlind = this.blindConfig.initialSmallBlind;
            bigBlind = this.blindConfig.initialBigBlind;
            console.log(`[TexasHoldemEngine] ${this.blindConfig.mode} mode - Blinds: $${smallBlind}/$${bigBlind}, Increase: ${this.blindConfig.increaseFrequency}, Multiplier: ${this.blindConfig.increaseMultiplier}x`);
        }

        // Create 6 players (player + 5 AI)
        const players: Player[] = [
            {
                id: 0,
                name: playerName,
                avatar: 'player',
                chips: playerChips,
                holeCards: [],
                isActive: true,
                isFolded: false,
                isAllIn: false,
                currentBet: 0,
                position: 0
            },
            {
                id: 1,
                name: 'Ace',
                avatar: 'cowboy',
                chips: playerChips,
                holeCards: [],
                isActive: true,
                isFolded: false,
                isAllIn: false,
                currentBet: 0,
                personality: 'aggressive',
                position: 1
            },
            {
                id: 2,
                name: 'Luna',
                avatar: 'woman',
                chips: playerChips,
                holeCards: [],
                isActive: true,
                isFolded: false,
                isAllIn: false,
                currentBet: 0,
                personality: 'tight',
                position: 2
            },
            {
                id: 3,
                name: 'Maverick',
                avatar: 'gambler',
                chips: playerChips,
                holeCards: [],
                isActive: true,
                isFolded: false,
                isAllIn: false,
                currentBet: 0,
                personality: 'loose',
                position: 3
            },
            {
                id: 4,
                name: 'Professor',
                avatar: 'oldman',
                chips: playerChips,
                holeCards: [],
                isActive: true,
                isFolded: false,
                isAllIn: false,
                currentBet: 0,
                personality: 'cautious',
                position: 4
            },
            {
                id: 5,
                name: 'Wild Card',
                avatar: 'joker',
                chips: playerChips,
                holeCards: [],
                isActive: true,
                isFolded: false,
                isAllIn: false,
                currentBet: 0,
                personality: 'unpredictable',
                position: 5
            }
        ];

        return {
            players,
            dealerPosition: 0,
            smallBlindPosition: 1,
            bigBlindPosition: 2,
            currentPlayerIndex: 3, // first to act pre-flop (after big blind)
            smallBlind: smallBlind,
            bigBlind: bigBlind,
            phase: 'pre-flop',
            communityCards: [],
            pot: 0,
            currentBet: 0,
            handNumber: 1,
            actionsThisRound: 0,
            deck: this.createDeck()
        };
    }

    private createDeck(): Card[] {
        const deck: Card[] = [];
        const suits: Array<'hearts' | 'diamonds' | 'clubs' | 'spades'> = ['hearts', 'diamonds', 'clubs', 'spades'];

        for (const suit of suits) {
            for (let rank = 2; rank <= 14; rank++) {
                deck.push({ rank, suit });
            }
        }

        return this.shuffleDeck(deck);
    }

    private shuffleDeck(deck: Card[]): Card[] {
        const shuffled = [...deck];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    public startNewHand(): void {
        // Reset players and check for eliminations FIRST
        const eliminatedPlayers: Player[] = [];
        this.state.players.forEach(player => {
            player.holeCards = [];
            const wasActive = player.isActive;
            player.isActive = player.chips > 0;
            player.isFolded = false;
            player.isAllIn = false;
            player.currentBet = 0;

            // Track newly eliminated players
            if (wasActive && !player.isActive) {
                eliminatedPlayers.push(player);
            }
        });

        // Rotate dealer position, skipping eliminated players
        this.state.dealerPosition = this.getNextActivePosition(this.state.dealerPosition);
        this.state.smallBlindPosition = this.getNextActivePosition(this.state.dealerPosition);
        this.state.bigBlindPosition = this.getNextActivePosition(this.state.smallBlindPosition);

        console.log(`[startNewHand] Dealer: ${this.state.dealerPosition}, SB: ${this.state.smallBlindPosition}, BB: ${this.state.bigBlindPosition}`);

        // Reset state for new hand
        this.state.deck = this.createDeck();
        this.state.communityCards = [];
        this.state.pot = 0;
        this.state.currentBet = 0;
        this.state.phase = 'pre-flop';
        this.state.actionsThisRound = 0;

        // Handle blind increases based on configuration
        this.handleBlindIncrease(eliminatedPlayers);

        // Track hands for hand-based increases
        this.handsSinceLastBlindIncrease++;

        // Post blinds
        this.postBlinds();

        // Deal hole cards
        this.dealHoleCards();

        // Set first player to act (after big blind)
        this.state.currentPlayerIndex = (this.state.bigBlindPosition + 1) % this.state.players.length;
        this.skipInactivePlayers();

        this.state.handNumber++;
    }

    private postBlinds(): void {
        const sbPlayer = this.state.players[this.state.smallBlindPosition];
        const bbPlayer = this.state.players[this.state.bigBlindPosition];

        // Small blind
        const sbAmount = Math.min(this.state.smallBlind, sbPlayer.chips);
        sbPlayer.chips -= sbAmount;
        sbPlayer.currentBet = sbAmount;
        this.state.pot += sbAmount;
        if (sbPlayer.chips === 0) sbPlayer.isAllIn = true;

        // Big blind
        const bbAmount = Math.min(this.state.bigBlind, bbPlayer.chips);
        bbPlayer.chips -= bbAmount;
        bbPlayer.currentBet = bbAmount;
        this.state.pot += bbAmount;
        if (bbPlayer.chips === 0) bbPlayer.isAllIn = true;

        this.state.currentBet = bbAmount;
    }

    private handleBlindIncrease(eliminatedPlayers: Player[]): void {
        let shouldIncrease = false;

        // Determine if blinds should increase based on configuration
        switch (this.blindConfig.increaseFrequency) {
            case 'elimination':
                // Increase if players were eliminated this hand
                if (eliminatedPlayers.length > 0) {
                    shouldIncrease = true;
                    if (this.blindConfig.mode === 'default') {
                        // Default mode: double for each elimination (legacy behavior)
                        const multiplier = Math.pow(2, eliminatedPlayers.length);
                        this.state.smallBlind *= multiplier;
                        this.state.bigBlind *= multiplier;
                        console.log(`[handleBlindIncrease] Default elimination - ${eliminatedPlayers.length} eliminated, multiplier: ${multiplier}x, new blinds: $${this.state.smallBlind}/$${this.state.bigBlind}`);
                    } else {
                        // Fixed/Custom mode: use configured multiplier
                        this.state.smallBlind = Math.floor(this.state.smallBlind * this.blindConfig.increaseMultiplier);
                        this.state.bigBlind = Math.floor(this.state.bigBlind * this.blindConfig.increaseMultiplier);
                        console.log(`[handleBlindIncrease] Elimination-based - ${eliminatedPlayers.length} eliminated, multiplier: ${this.blindConfig.increaseMultiplier}x, new blinds: $${this.state.smallBlind}/$${this.state.bigBlind}`);
                    }
                }
                break;

            case 'hands':
                // Increase based on hands played
                const handsPerIncrease = this.blindConfig.handsPerIncrease || 10;
                if (this.handsSinceLastBlindIncrease >= handsPerIncrease) {
                    shouldIncrease = true;
                    this.state.smallBlind = Math.floor(this.state.smallBlind * this.blindConfig.increaseMultiplier);
                    this.state.bigBlind = Math.floor(this.state.bigBlind * this.blindConfig.increaseMultiplier);
                    this.handsSinceLastBlindIncrease = 0;
                    console.log(`[handleBlindIncrease] Hand-based - every ${handsPerIncrease} hands, multiplier: ${this.blindConfig.increaseMultiplier}x, new blinds: $${this.state.smallBlind}/$${this.state.bigBlind}`);
                }
                break;

            case 'none':
                // Never increase blinds
                console.log(`[handleBlindIncrease] No increase - blinds remain at $${this.state.smallBlind}/$${this.state.bigBlind}`);
                break;
        }
    }

    private dealHoleCards(): void {
        // Deal 2 cards to each active player
        for (let i = 0; i < 2; i++) {
            for (const player of this.state.players) {
                if (player.isActive) {
                    player.holeCards.push(this.state.deck.pop()!);
                }
            }
        }
    }

    public playerAction(action: PlayerAction, raiseAmount?: number): boolean {
        const player = this.state.players[this.state.currentPlayerIndex];

        if (!player.isActive || player.isFolded) {
            return false;
        }

        switch (action) {
            case 'fold':
                player.isFolded = true;
                player.isActive = false;
                break;

            case 'check':
                if (player.currentBet < this.state.currentBet) {
                    return false; // Can't check if there's a bet to call
                }
                break;

            case 'call':
                const callAmount = this.state.currentBet - player.currentBet;
                const actualCall = Math.min(callAmount, player.chips);
                player.chips -= actualCall;
                player.currentBet += actualCall;
                this.state.pot += actualCall;
                if (player.chips === 0) player.isAllIn = true;
                break;

            case 'raise':
                if (!raiseAmount || raiseAmount <= 0) return false;
                const totalRaise = this.state.currentBet - player.currentBet + raiseAmount;
                const actualRaise = Math.min(totalRaise, player.chips);
                player.chips -= actualRaise;
                player.currentBet += actualRaise;
                this.state.pot += actualRaise;
                this.state.currentBet = player.currentBet;
                if (player.chips === 0) player.isAllIn = true;
                // IMPORTANT: When someone raises, reset action counter so everyone gets a chance to respond
                console.log('[RAISE] Resetting actions counter from', this.state.actionsThisRound, 'to 0 (will become 1 after increment)');
                this.state.actionsThisRound = 0; // Will become 1 after the increment below
                break;

            case 'all-in':
                const allInAmount = player.chips;
                player.chips = 0;
                player.currentBet += allInAmount;
                this.state.pot += allInAmount;
                if (player.currentBet > this.state.currentBet) {
                    // All-in is effectively a raise if it increases the current bet
                    this.state.currentBet = player.currentBet;
                    console.log('[ALL-IN RAISE] Resetting actions counter from', this.state.actionsThisRound, 'to 0 (will become 1 after increment)');
                    this.state.actionsThisRound = 0; // Will become 1 after the increment below
                }
                player.isAllIn = true;
                break;
        }

        this.state.actionsThisRound++;
        this.moveToNextPlayer();
        return true;
    }

    private moveToNextPlayer(): void {
        // Check if betting round is complete
        if (this.isBettingRoundComplete()) {
            this.advanceToNextPhase();
            return;
        }

        // Move to next active player
        let attempts = 0;
        do {
            this.state.currentPlayerIndex = (this.state.currentPlayerIndex + 1) % this.state.players.length;
            attempts++;

            // Safety check to prevent infinite loop
            if (attempts > this.state.players.length) {
                // No one can act, advance phase
                this.advanceToNextPhase();
                return;
            }
        } while (!this.canPlayerAct(this.state.players[this.state.currentPlayerIndex]));
    }

    private skipInactivePlayers(): void {
        let attempts = 0;
        while (!this.canPlayerAct(this.state.players[this.state.currentPlayerIndex])) {
            this.state.currentPlayerIndex = (this.state.currentPlayerIndex + 1) % this.state.players.length;
            attempts++;

            // Safety check to prevent infinite loop
            if (attempts > this.state.players.length) {
                // No one can act, just break
                break;
            }
        }
    }

    private canPlayerAct(player: Player): boolean {
        return player.isActive && !player.isFolded && !player.isAllIn;
    }

    /**
     * Get the next active player position (skipping eliminated players)
     * Used for rotating dealer and blind positions
     */
    private getNextActivePosition(currentPosition: number): number {
        let nextPosition = (currentPosition + 1) % this.state.players.length;
        let attempts = 0;

        // Keep moving until we find an active player
        while (!this.state.players[nextPosition].isActive && attempts < this.state.players.length) {
            nextPosition = (nextPosition + 1) % this.state.players.length;
            attempts++;
        }

        // Safety check - if no active players found, return current position
        if (attempts >= this.state.players.length) {
            console.error('[getNextActivePosition] No active players found!');
            return currentPosition;
        }

        return nextPosition;
    }

    private isBettingRoundComplete(): boolean {
        const activePlayers = this.state.players.filter(p => p.isActive && !p.isFolded);

        console.log('[isBettingRoundComplete] Checking...');
        console.log('  Active players:', activePlayers.length);
        console.log('  Actions this round:', this.state.actionsThisRound);
        console.log('  Current bet:', this.state.currentBet);

        // If only one player remains, hand is over
        if (activePlayers.length === 1) {
            console.log('  -> TRUE: Only one player left');
            return true;
        }

        // Check if all active players have acted and matched the current bet
        const playersWhoCanAct = activePlayers.filter(p => !p.isAllIn);
        console.log('  Players who can act:', playersWhoCanAct.length);

        if (playersWhoCanAct.length === 0) {
            // Everyone is all-in, move to showdown
            console.log('  -> TRUE: Everyone is all-in');
            return true;
        }

        if (playersWhoCanAct.length === 1) {
            // Only one player can act and everyone else is all-in or folded
            // If that player has matched the highest bet, round is complete
            const soloPlayer = playersWhoCanAct[0];
            const hasMatched = soloPlayer.currentBet >= this.state.currentBet;
            const hasActed = this.state.actionsThisRound >= activePlayers.length;
            console.log('  Solo player:', soloPlayer.name, 'hasMatched:', hasMatched, 'hasActed:', hasActed);
            console.log('  -> ' + (hasMatched && hasActed ? 'TRUE' : 'FALSE'));
            return hasMatched && hasActed;
        }

        // Everyone who can act has acted and matched the bet
        const allMatched = playersWhoCanAct.every(p => p.currentBet === this.state.currentBet);
        const allActed = this.state.actionsThisRound >= playersWhoCanAct.length;

        console.log('  Player bets:', playersWhoCanAct.map(p => `${p.name}: $${p.currentBet}`).join(', '));
        console.log('  All matched:', allMatched);
        console.log('  All acted:', allActed, `(${this.state.actionsThisRound} >= ${playersWhoCanAct.length})`);
        console.log('  -> ' + (allMatched && allActed ? 'TRUE' : 'FALSE'));

        return allMatched && allActed;
    }

    private advanceToNextPhase(): void {
        // Reset bets for next round
        this.state.players.forEach(player => {
            player.currentBet = 0;
        });
        this.state.currentBet = 0;
        this.state.actionsThisRound = 0;

        // Check if hand is over (only one player left)
        const activePlayers = this.state.players.filter(p => p.isActive && !p.isFolded);
        if (activePlayers.length === 1) {
            this.state.phase = 'hand-over';
            this.awardPot([{
                playerId: activePlayers[0].id,
                playerName: activePlayers[0].name,
                amount: this.state.pot,
                handName: 'Winner by default'
            }]);
            return;
        }

        // Advance phase
        switch (this.state.phase) {
            case 'pre-flop':
                this.dealFlop();
                this.state.phase = 'flop';
                break;
            case 'flop':
                this.dealTurn();
                this.state.phase = 'turn';
                break;
            case 'turn':
                this.dealRiver();
                this.state.phase = 'river';
                break;
            case 'river':
                this.state.phase = 'showdown';
                this.evaluateShowdown();
                return;
        }

        // Start betting from small blind position (or first active after dealer)
        this.state.currentPlayerIndex = (this.state.dealerPosition + 1) % this.state.players.length;
        this.skipInactivePlayers();
    }

    private dealFlop(): void {
        this.state.deck.pop(); // Burn card
        for (let i = 0; i < 3; i++) {
            this.state.communityCards.push(this.state.deck.pop()!);
        }
    }

    private dealTurn(): void {
        this.state.deck.pop(); // Burn card
        this.state.communityCards.push(this.state.deck.pop()!);
    }

    private dealRiver(): void {
        this.state.deck.pop(); // Burn card
        this.state.communityCards.push(this.state.deck.pop()!);
    }

    private evaluateShowdown(): void {
        const activePlayers = this.state.players.filter(p => p.isActive && !p.isFolded);
        const handResults: HandResult[] = [];

        for (const player of activePlayers) {
            const result = this.evaluateHand(player.holeCards, this.state.communityCards);
            handResults.push({
                playerId: player.id,
                ...result
            });
        }

        // Sort by hand rank (descending)
        handResults.sort((a, b) => {
            if (b.handRank !== a.handRank) return b.handRank - a.handRank;

            // Compare best cards
            for (let i = 0; i < 5; i++) {
                if (b.bestCards[i].rank !== a.bestCards[i].rank) {
                    return b.bestCards[i].rank - a.bestCards[i].rank;
                }
            }

            return 0;
        });

        // Determine winners (handle ties)
        const winners: PotWinner[] = [];
        const winningRank = handResults[0].handRank;

        for (const result of handResults) {
            if (result.handRank === winningRank && this.handsAreEqual(result, handResults[0])) {
                const player = this.state.players.find(p => p.id === result.playerId)!;
                winners.push({
                    playerId: result.playerId,
                    playerName: player.name,
                    amount: 0, // Will be calculated
                    handName: result.handName
                });
            } else {
                break;
            }
        }

        // Split pot among winners
        const amountPerWinner = Math.floor(this.state.pot / winners.length);
        winners.forEach(winner => winner.amount = amountPerWinner);

        this.awardPot(winners);
        this.state.phase = 'hand-over';
    }

    private handsAreEqual(hand1: HandResult, hand2: HandResult): boolean {
        if (hand1.handRank !== hand2.handRank) return false;

        for (let i = 0; i < 5; i++) {
            if (hand1.bestCards[i].rank !== hand2.bestCards[i].rank) {
                return false;
            }
        }

        return true;
    }

    private awardPot(winners: PotWinner[]): void {
        // Store winners for display
        this.state.lastWinners = winners;

        for (const winner of winners) {
            const player = this.state.players.find(p => p.id === winner.playerId);
            if (player) {
                player.chips += winner.amount;
            }
        }
    }

    // Hand evaluation logic
    private evaluateHand(holeCards: Card[], communityCards: Card[]): Omit<HandResult, 'playerId'> {
        const allCards = [...holeCards, ...communityCards];

        // Generate all possible 5-card combinations
        const combinations = this.getCombinations(allCards, 5);

        let bestHand: Omit<HandResult, 'playerId'> | null = null;

        for (const combo of combinations) {
            const hand = this.evaluateFiveCardHand(combo);

            if (!bestHand || hand.handRank > bestHand.handRank ||
                (hand.handRank === bestHand.handRank && this.compareCards(hand.bestCards, bestHand.bestCards) > 0)) {
                bestHand = hand;
            }
        }

        return bestHand!;
    }

    private getCombinations(arr: Card[], k: number): Card[][] {
        if (k === 1) return arr.map(c => [c]);

        const combos: Card[][] = [];
        for (let i = 0; i <= arr.length - k; i++) {
            const head = arr[i];
            const tailCombos = this.getCombinations(arr.slice(i + 1), k - 1);
            for (const tail of tailCombos) {
                combos.push([head, ...tail]);
            }
        }

        return combos;
    }

    private evaluateFiveCardHand(cards: Card[]): Omit<HandResult, 'playerId'> {
        const sorted = [...cards].sort((a, b) => b.rank - a.rank);

        const isFlush = cards.every(c => c.suit === cards[0].suit);
        const isStraight = this.isStraight(sorted);
        const ranks = sorted.map(c => c.rank);
        const rankCounts = this.getRankCounts(ranks);

        // Royal Flush
        if (isFlush && isStraight && sorted[0].rank === 14) {
            return { handName: 'Royal Flush', handRank: 10, bestCards: sorted, kickerCards: [] };
        }

        // Straight Flush
        if (isFlush && isStraight) {
            return { handName: 'Straight Flush', handRank: 9, bestCards: sorted, kickerCards: [] };
        }

        // Four of a Kind
        if (rankCounts.some(c => c.count === 4)) {
            const fourRank = rankCounts.find(c => c.count === 4)!.rank;
            // Build bestCards in correct order: [quad, quad, quad, quad, kicker]
            const quads = sorted.filter(c => c.rank === fourRank);
            const kicker = sorted.find(c => c.rank !== fourRank)!;
            const orderedCards = [...quads, kicker];

            return {
                handName: 'Four of a Kind',
                handRank: 8,
                bestCards: orderedCards,
                kickerCards: []
            };
        }

        // Full House
        if (rankCounts.some(c => c.count === 3) && rankCounts.some(c => c.count === 2)) {
            const threeRank = rankCounts.find(c => c.count === 3)!.rank;
            const pairRank = rankCounts.find(c => c.count === 2)!.rank;
            // Build bestCards in correct order: [trip, trip, trip, pair, pair]
            const trips = sorted.filter(c => c.rank === threeRank);
            const pair = sorted.filter(c => c.rank === pairRank);
            const orderedCards = [...trips, ...pair];

            return { handName: 'Full House', handRank: 7, bestCards: orderedCards, kickerCards: [] };
        }

        // Flush
        if (isFlush) {
            return { handName: 'Flush', handRank: 6, bestCards: sorted, kickerCards: [] };
        }

        // Straight
        if (isStraight) {
            return { handName: 'Straight', handRank: 5, bestCards: sorted, kickerCards: [] };
        }

        // Three of a Kind
        if (rankCounts.some(c => c.count === 3)) {
            const threeRank = rankCounts.find(c => c.count === 3)!.rank;
            // Build bestCards in correct order: [trip, trip, trip, kicker1, kicker2]
            const trips = sorted.filter(c => c.rank === threeRank);
            const kickers = sorted.filter(c => c.rank !== threeRank).sort((a, b) => b.rank - a.rank);
            const orderedCards = [...trips, ...kickers];

            return { handName: 'Three of a Kind', handRank: 4, bestCards: orderedCards, kickerCards: [] };
        }

        // Two Pair
        const pairs = rankCounts.filter(c => c.count === 2);
        if (pairs.length === 2) {
            // Sort pairs by rank (higher pair first)
            const sortedPairs = pairs.sort((a, b) => b.rank - a.rank);
            const higherPairRank = sortedPairs[0].rank;
            const lowerPairRank = sortedPairs[1].rank;

            // Build bestCards in correct order: [higher_pair, higher_pair, lower_pair, lower_pair, kicker]
            const higherPair = sorted.filter(c => c.rank === higherPairRank);
            const lowerPair = sorted.filter(c => c.rank === lowerPairRank);
            const kicker = sorted.find(c => c.rank !== higherPairRank && c.rank !== lowerPairRank)!;
            const orderedCards = [...higherPair, ...lowerPair, kicker];

            return { handName: 'Two Pair', handRank: 3, bestCards: orderedCards, kickerCards: [] };
        }

        // One Pair
        if (pairs.length === 1) {
            const pairRank = pairs[0].rank;
            // Build bestCards in correct order: [pair, pair, kicker1, kicker2, kicker3]
            const pair = sorted.filter(c => c.rank === pairRank);
            const kickers = sorted.filter(c => c.rank !== pairRank).sort((a, b) => b.rank - a.rank);
            const orderedCards = [...pair, ...kickers];

            return { handName: 'Pair', handRank: 2, bestCards: orderedCards, kickerCards: [] };
        }

        // High Card
        return { handName: 'High Card', handRank: 1, bestCards: sorted, kickerCards: [] };
    }

    private isStraight(sortedCards: Card[]): boolean {
        // Check for regular straight
        let isStraight = true;
        for (let i = 0; i < sortedCards.length - 1; i++) {
            if (sortedCards[i].rank - sortedCards[i + 1].rank !== 1) {
                isStraight = false;
                break;
            }
        }

        // Check for A-2-3-4-5 (wheel)
        if (!isStraight && sortedCards[0].rank === 14) {
            const ranks = sortedCards.map(c => c.rank);
            if (ranks[1] === 5 && ranks[2] === 4 && ranks[3] === 3 && ranks[4] === 2) {
                return true;
            }
        }

        return isStraight;
    }

    private getRankCounts(ranks: number[]): Array<{ rank: number; count: number }> {
        const counts = new Map<number, number>();

        for (const rank of ranks) {
            counts.set(rank, (counts.get(rank) || 0) + 1);
        }

        return Array.from(counts.entries())
            .map(([rank, count]) => ({ rank, count }))
            .sort((a, b) => b.count - a.count || b.rank - a.rank);
    }

    private compareCards(cards1: Card[], cards2: Card[]): number {
        for (let i = 0; i < Math.min(cards1.length, cards2.length); i++) {
            if (cards1[i].rank !== cards2[i].rank) {
                return cards1[i].rank - cards2[i].rank;
            }
        }
        return 0;
    }

    // AI Decision Making
    public getAIDecision(playerId: number): AIDecision {
        const player = this.state.players.find(p => p.id === playerId);
        if (!player || !player.personality) {
            return { action: 'fold', reasoning: '...' };
        }

        const callAmount = this.state.currentBet - player.currentBet;
        const potOdds = callAmount / (this.state.pot + callAmount);

        // Evaluate hand strength (simplified)
        const handStrength = this.evaluateHandStrength(player.holeCards, this.state.communityCards);

        switch (player.personality) {
            case 'aggressive':
                return this.getAggressiveDecision(player, handStrength, callAmount, potOdds);
            case 'tight':
                return this.getTightDecision(player, handStrength, callAmount, potOdds);
            case 'loose':
                return this.getLooseDecision(player, handStrength, callAmount, potOdds);
            case 'cautious':
                return this.getCautiousDecision(player, handStrength, callAmount, potOdds);
            case 'unpredictable':
                return this.getUnpredictableDecision(player, handStrength, callAmount, potOdds);
            default:
                return { action: 'fold', reasoning: '...' };
        }
    }

    private evaluateHandStrength(holeCards: Card[], communityCards: Card[]): number {
        if (communityCards.length === 0) {
            // Pre-flop hand strength
            return this.evaluatePreFlopStrength(holeCards);
        }

        const result = this.evaluateHand(holeCards, communityCards);
        return result.handRank / 10; // Normalize to 0-1 range
    }

    private evaluatePreFlopStrength(holeCards: Card[]): number {
        if (holeCards.length < 2) return 0;

        const [card1, card2] = holeCards;
        const isPair = card1.rank === card2.rank;
        const isSuited = card1.suit === card2.suit;
        const highCard = Math.max(card1.rank, card2.rank);

        let strength = 0;

        if (isPair) {
            strength = 0.5 + (highCard / 28); // Pairs are strong
        } else {
            strength = (highCard + card2.rank) / 56;
            if (isSuited) strength += 0.1;
        }

        return Math.min(strength, 1);
    }

    private getAggressiveDecision(player: Player, handStrength: number, callAmount: number, potOdds: number): AIDecision {
        // Always check if it's free
        if (callAmount === 0) {
            if (handStrength > 0.7 && Math.random() < 0.4) {
                const raiseAmount = Math.min(this.state.pot * 0.5, player.chips);
                if (raiseAmount >= this.state.bigBlind) {
                    return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
                }
            }
            return { action: 'check', reasoning: getPlayerDialogue(player.name, 'check') };
        }

        if (handStrength > 0.7) {
            const raiseAmount = Math.min(this.state.pot * 0.5, player.chips - callAmount);
            if (raiseAmount >= this.state.bigBlind) {
                return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
            }
        }

        if (handStrength > 0.4 || Math.random() < 0.3) {
            return { action: 'call', reasoning: getPlayerDialogue(player.name, 'call') };
        }

        return { action: 'fold', reasoning: getPlayerDialogue(player.name, 'fold') };
    }

    private getTightDecision(player: Player, handStrength: number, callAmount: number, potOdds: number): AIDecision {
        // Always check if it's free
        if (callAmount === 0) {
            if (handStrength > 0.8) {
                const raiseAmount = Math.min(this.state.pot * 0.3, player.chips);
                if (raiseAmount >= this.state.bigBlind) {
                    return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
                }
            }
            return { action: 'check', reasoning: getPlayerDialogue(player.name, 'check') };
        }

        if (handStrength > 0.8) {
            const raiseAmount = Math.min(this.state.pot * 0.3, player.chips - callAmount);
            if (raiseAmount >= this.state.bigBlind) {
                return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
            }
        }

        if (handStrength > 0.6 && handStrength > potOdds) {
            return { action: 'call', reasoning: getPlayerDialogue(player.name, 'call') };
        }

        return { action: 'fold', reasoning: getPlayerDialogue(player.name, 'fold') };
    }

    private getLooseDecision(player: Player, handStrength: number, callAmount: number, potOdds: number): AIDecision {
        // Always check if it's free
        if (callAmount === 0) {
            if (Math.random() < 0.2) {
                const raiseAmount = Math.min(this.state.bigBlind * 3, player.chips);
                if (raiseAmount >= this.state.bigBlind) {
                    return { action: 'raise', raiseAmount, reasoning: getPlayerDialogue(player.name, 'raise') };
                }
            }
            return { action: 'check', reasoning: getPlayerDialogue(player.name, 'check') };
        }

        if (Math.random() < 0.2) {
            const raiseAmount = Math.min(this.state.bigBlind * 3, player.chips - callAmount);
            if (raiseAmount >= this.state.bigBlind) {
                return { action: 'raise', raiseAmount, reasoning: getPlayerDialogue(player.name, 'raise') };
            }
        }

        if (handStrength > 0.3 || Math.random() < 0.5) {
            return { action: 'call', reasoning: getPlayerDialogue(player.name, 'call') };
        }

        return { action: 'fold', reasoning: getPlayerDialogue(player.name, 'fold') };
    }

    private getCautiousDecision(player: Player, handStrength: number, callAmount: number, potOdds: number): AIDecision {
        // Always check if it's free
        if (callAmount === 0) {
            if (handStrength > 0.85) {
                const raiseAmount = Math.min(this.state.pot * 0.25, player.chips);
                if (raiseAmount >= this.state.bigBlind) {
                    return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
                }
            }
            return { action: 'check', reasoning: getPlayerDialogue(player.name, 'check') };
        }

        if (handStrength > 0.85) {
            const raiseAmount = Math.min(this.state.pot * 0.25, player.chips - callAmount);
            if (raiseAmount >= this.state.bigBlind) {
                return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
            }
        }

        if (handStrength > 0.7 && handStrength > potOdds * 2) {
            return { action: 'call', reasoning: getPlayerDialogue(player.name, 'call') };
        }

        return { action: 'fold', reasoning: getPlayerDialogue(player.name, 'fold') };
    }

    private getUnpredictableDecision(player: Player, handStrength: number, callAmount: number, potOdds: number): AIDecision {
        const random = Math.random();

        // Always check if it's free
        if (callAmount === 0) {
            if (random < 0.15) {
                const raiseAmount = Math.min(Math.random() * player.chips * 0.5, player.chips);
                if (raiseAmount >= this.state.bigBlind) {
                    return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
                }
            }
            return { action: 'check', reasoning: getPlayerDialogue(player.name, 'check') };
        }

        if (random < 0.15) {
            const raiseAmount = Math.min(Math.random() * player.chips * 0.5, player.chips - callAmount);
            if (raiseAmount >= this.state.bigBlind) {
                return { action: 'raise', raiseAmount: Math.floor(raiseAmount), reasoning: getPlayerDialogue(player.name, 'raise') };
            }
        }

        if (random < 0.6) {
            return { action: 'call', reasoning: getPlayerDialogue(player.name, 'call') };
        }

        return { action: 'fold', reasoning: getPlayerDialogue(player.name, 'fold') };
    }

    public getState(): HoldemGameState {
        return { ...this.state };
    }

    public getCurrentPlayer(): Player {
        return this.state.players[this.state.currentPlayerIndex];
    }

    public getPlayer(id: number): Player | undefined {
        return this.state.players.find(p => p.id === id);
    }

    public isPlayerTurn(playerId: number): boolean {
        return this.state.players[this.state.currentPlayerIndex].id === playerId;
    }

    public getActivePlayers(): Player[] {
        return this.state.players.filter(p => p.isActive && !p.isFolded);
    }
}
